(() => {
  "use strict";

  // Kill-switch de test : ?epass=off désactive TOUT l'écosystème EPASS
  // (pour isoler si nos scripts gênent Kaching). À retirer une fois diagnostiqué.
  try { if (/[?&]epass=off\b/i.test(location.search)) return; } catch (_) {}

  // =========================================================
  // EPASS CORE — orchestrateur unique de la couche transport
  //
  // POURQUOI ce fichier :
  // Avant, 4 scripts (google-font / google-front-font / epass-cart-events /
  // epass-gbp-service) patchaient TOUS window.fetch chacun de leur côté, +
  // un watchdog qui réordonnait fetch toutes les 500ms. L'ordre des wrappers
  // n'était donc jamais déterministe -> "parfois 1 article plein tarif,
  // parfois le pack sans réconciliation".
  //
  // Ici on installe UN SEUL wrapper fetch + UN SEUL wrapper XHR, le plus tôt
  // possible (chargé en <head> avant Kaching), qui :
  //   1) injecte les payloads cachés (_epass_personalization / _epass_gbp_service)
  //      sur les /cart/add sortants (filet, car ils transitent déjà via le form) ;
  //   2) après CHAQUE mutation panier (add/change/update), déclenche la
  //      réconciliation via window.EPASS_RECONCILE.run() (algo inchangé) ;
  //   3) émet les events epass:add_to_cart:done / :finished / epass:cart:reconciled.
  //
  // Filets de sécurité (au cas où Kaching capture fetch avant nous) :
  //   - écoute le PUB_SUB cartUpdate du thème ;
  //   - re-vérifie le panier après un clic sur "ajouter au panier".
  //
  // La réconciliation est idempotente : la rappeler "pour rien" ne fait rien.
  // =========================================================

  const W = window;

  // Idempotent : ce fichier ne s'installe qu'une fois.
  if (W.__EPASS_CORE_INSTALLED__) return;
  W.__EPASS_CORE_INSTALLED__ = true;

  const DEBUG = !!W.EPASS_DEBUG;
  const log = (...a) => DEBUG && console.log("[EPASS-CORE]", ...a);
  const warn = (...a) => console.warn("[EPASS-CORE]", ...a);

  // --- Constantes ------------------------------------------------
  const PERSONALIZATION_HIDDEN_ID = "epass-personalization-json";
  const GBP_HIDDEN_ID = "epass-gbp-service-json";

  const PERSONALIZATION_PROP =
    typeof W.EPASS_PROPERTY_NAME === "string" && W.EPASS_PROPERTY_NAME.trim()
      ? W.EPASS_PROPERTY_NAME.trim()
      : "_epass_personalization";
  const GBP_PROP = "_epass_gbp_service";

  const CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/i;
  const CART_MUTATION_RE = /\/cart\/(add|change|update)(\.js)?(\?|$)/i;

  // =========================================================
  // Helpers généraux
  // =========================================================
  function isInternal() {
    return !!W.__EPASS_INTERNAL_CART_MUTATION__;
  }

  function emit(type, detail) {
    try {
      document.dispatchEvent(new CustomEvent(type, { detail }));
    } catch (e) {
      warn("emit error", type, e);
    }
  }

  function urlOf(input) {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
    return "";
  }

  function methodOf(input, init) {
    const m =
      (init && init.method) ||
      (input && input.method) ||
      "GET";
    return String(m).toUpperCase();
  }

  // Force la resynchro des champs cachés si les modules UI l'exposent
  function hardSyncHidden() {
    try {
      if (W.EPASS_PERSONALIZER && typeof W.EPASS_PERSONALIZER.syncHidden === "function") {
        W.EPASS_PERSONALIZER.syncHidden();
      }
    } catch (_) {}
    try {
      if (W.EPASS_GBP && typeof W.EPASS_GBP.syncHidden === "function") {
        W.EPASS_GBP.syncHidden();
      }
    } catch (_) {}
  }

  function readHidden(id) {
    const el = document.getElementById(id);
    return el && typeof el.value === "string" ? el.value.trim() : "";
  }

  function collectInjectableProps() {
    hardSyncHidden();
    const out = {};
    const p = readHidden(PERSONALIZATION_HIDDEN_ID);
    if (p) out[PERSONALIZATION_PROP] = p;
    const g = readHidden(GBP_HIDDEN_ID);
    if (g) out[GBP_PROP] = g;
    return out;
  }

  // Mémorise le payload de perso (champ caché) au moment de l'ajout. Le
  // réconciliateur l'utilisera comme source si Kaching ne l'attache à aucune
  // ligne (cas des bundles multi-lignes / des 17 formulaires de la page).
  function stashPendingPayload() {
    try {
      hardSyncHidden();
      const v = readHidden(PERSONALIZATION_HIDDEN_ID);
      if (v) W.EPASS_PENDING_PAYLOAD = v;
    } catch (_) {}
  }

  // =========================================================
  // INJECTION À LA SOURCE (le nouveau système propre)
  // On écrit les attributs visibles directement dans la requête /cart/add.js
  // de Kaching, sur CHAQUE item, AVANT qu'elle parte. Chaque ligne naît déjà
  // correcte -> aucune réconciliation, aucune requête panier en plus, aucune
  // course. Le `__kaching_bundles` (donc le prix) n'est jamais touché.
  // =========================================================
  function epassExtractPrimaryValue(slot) {
    const cands = [
      slot && slot.profile, slot && slot.phone, slot && slot.number,
      slot && slot.username, slot && slot.handle, slot && slot.value,
      slot && slot.text, slot && slot.input, slot && slot.url, slot && slot.link,
    ];
    for (const c of cands) {
      if (c === null || c === undefined) continue;
      const s = String(c).trim();
      if (s) return s;
    }
    return "";
  }

  // Attributs visibles d'un slot (mêmes clés que l'historique : E-Pass / Profil
  // / Établissement / Ville / _link). `_link` reste masqué (préfixe _).
  function epassVisibleFromSlot(slot) {
    const props = {};
    const platform = slot && slot.platform ? String(slot.platform).trim() : "";
    const deferred = slot && slot.deferred === true;

    props["E-Pass"] = platform || "Configuration";
    if (deferred) {
      props["Configuration"] = "À configurer plus tard";
      return props;
    }
    const primary = epassExtractPrimaryValue(slot);
    if (primary) props["Profil"] = primary;
    if (slot && slot.place_name) props["Établissement"] = String(slot.place_name);
    if (slot && slot.city) props["Ville"] = String(slot.city);
    if (slot && slot.review_url) props["_link"] = String(slot.review_url);
    return props;
  }

  function epassParseSlots(payloadRaw) {
    try {
      const obj = JSON.parse(payloadRaw);
      const slots = Array.isArray(obj && obj.slots) ? obj.slots.slice() : [];
      slots.sort((a, b) => (Number(a && a.slot) || 0) - (Number(b && b.slot) || 0));
      return slots;
    } catch (_) {
      return [];
    }
  }

  function epassIsKachingItem(item) {
    return !!(item && item.properties && typeof item.properties.__kaching_bundles === "string");
  }

  // Distribue les slots sur les items du bundle, par variant et dans l'ordre.
  // Renvoie true si au moins un item a reçu ses attributs.
  function epassInjectItems(items, payloadRaw) {
    if (!Array.isArray(items) || !items.length) return false;
    const slots = epassParseSlots(payloadRaw);
    if (!slots.length) return false;

    const queueByVariant = new Map();
    for (const slot of slots) {
      const vid = String((slot && slot.variant_id) || "").trim();
      if (!vid) continue;
      if (!queueByVariant.has(vid)) queueByVariant.set(vid, []);
      queueByVariant.get(vid).push(slot);
    }

    let changed = false;
    for (const item of items) {
      if (!epassIsKachingItem(item)) continue;
      const vid = String((item.id != null ? item.id : item.variant_id) || "").trim();
      const queue = queueByVariant.get(vid);
      if (!queue || !queue.length) continue;
      const visible = epassVisibleFromSlot(queue.shift());
      delete item.properties._epass_personalization;
      Object.assign(item.properties, visible);
      changed = true;
    }

    // Le blob brut n'a plus à voyager : on le retire de tous les items.
    for (const item of items) {
      if (item && item.properties) delete item.properties._epass_personalization;
    }

    return changed;
  }

  // Tente l'injection bundle sur la requête. Renvoie { init, injected } ou null.
  // Ne gère que les bodies JSON ({ items:[...] }) — le format de Kaching.
  // Tout autre cas -> null (on retombe sur le filet de réconciliation).
  function maybeInjectBundle(init) {
    if (!init || typeof init.body !== "string") return null;

    const payload =
      (W.EPASS_PENDING_PAYLOAD && String(W.EPASS_PENDING_PAYLOAD).trim()) ||
      readHidden(PERSONALIZATION_HIDDEN_ID);
    if (!payload) return null;

    let parsed;
    try {
      parsed = JSON.parse(init.body);
    } catch (_) {
      return null;
    }
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (!parsed.items.some(epassIsKachingItem)) return null;

    let changed = false;
    try {
      changed = epassInjectItems(parsed.items, payload);
    } catch (e) {
      warn("bundle inject error (ignored)", e);
      return null;
    }
    if (!changed) return null;

    log("injection à la source OK", parsed.items.length, "items");
    return { init: { ...init, body: JSON.stringify(parsed) }, injected: true };
  }

  // =========================================================
  // Injection des payloads sur /cart/add (filet de sécurité)
  // On NE touche PAS aux ajouts multi-lignes ({items:[...]}) : c'est
  // Kaching qui les construit avec ses propres `properties` par ligne,
  // et Shopify ignore les `properties` de premier niveau dans ce cas.
  // =========================================================
  function ensureHeadersObj(h) {
    if (!h) return {};
    if (h instanceof Headers) {
      const obj = {};
      h.forEach((v, k) => (obj[k] = v));
      return obj;
    }
    return { ...h };
  }

  function bodyHasItemsArray(parsed) {
    return !!(parsed && typeof parsed === "object" && Array.isArray(parsed.items));
  }

  // Renvoie un init éventuellement modifié (jamais d'exception qui casse l'ajout).
  function maybeInjectIntoInit(input, init) {
    // On n'injecte que sur un body présent dans init (URL string + init.body).
    if (typeof input !== "string" || !init || !init.body) return init;

    const props = collectInjectableProps();
    if (!Object.keys(props).length) return init;

    const body = init.body;
    const next = { ...init };

    try {
      if (body instanceof FormData) {
        // Ajout multi-lignes Kaching ? on laisse tel quel.
        for (const key of body.keys()) {
          if (/^items\[/.test(key)) return init;
        }
        for (const [name, val] of Object.entries(props)) {
          body.set(`properties[${name}]`, val);
        }
        next.body = body;
        return next;
      }

      if (body instanceof URLSearchParams) {
        if (body.has("items[0][id]")) return init;
        for (const [name, val] of Object.entries(props)) {
          body.set(`properties[${name}]`, val);
        }
        next.body = body;
        return next;
      }

      if (typeof body === "string") {
        const headers = ensureHeadersObj(next.headers);
        const ct = String(headers["Content-Type"] || headers["content-type"] || "").toLowerCase();

        if (ct.includes("application/json")) {
          const parsed = JSON.parse(body);
          if (bodyHasItemsArray(parsed)) return init;
          if (parsed && typeof parsed === "object") {
            parsed.properties =
              parsed.properties && typeof parsed.properties === "object" ? parsed.properties : {};
            for (const [name, val] of Object.entries(props)) {
              parsed.properties[name] = val;
            }
            next.body = JSON.stringify(parsed);
            headers["Content-Type"] = "application/json";
            next.headers = headers;
            return next;
          }
          return init;
        }

        // urlencoded (ou inconnu) -> on traite en query string
        const usp = new URLSearchParams(body);
        if (usp.has("items[0][id]")) return init;
        for (const [name, val] of Object.entries(props)) {
          usp.set(`properties[${name}]`, val);
        }
        next.body = usp.toString();
        headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
        next.headers = headers;
        return next;
      }
    } catch (e) {
      warn("inject error (ignored)", e);
      return init;
    }

    return init;
  }

  // =========================================================
  // Pipeline : réconciliation + events (debounce + garde réentrance)
  // =========================================================
  let pipelineTimer = null;
  let pipelineRunning = false;
  let pendingBase = null;
  let lastPipelineAt = 0;

  // Après un pipeline, on ignore les filets de sécurité pendant ce délai
  // pour ne pas spammer le panier (anti-429).
  const SAFETY_COOLDOWN_MS = 6000;

  function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  function schedulePipeline(base) {
    pendingBase = base || pendingBase || {};
    if (pipelineTimer) clearTimeout(pipelineTimer);
    pipelineTimer = setTimeout(runPipeline, 80);
  }

  // GET /cart.js résilient au 429 (backoff + Retry-After).
  async function safeGetCart() {
    W.__EPASS_INTERNAL_CART_MUTATION__ = true;
    try {
      for (let attempt = 0; ; attempt++) {
        const res = await nativeFetch("/cart.js", {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
          credentials: "same-origin",
        });
        if (res.status === 429 && attempt < 3) {
          const ra = Number(res.headers.get("Retry-After"));
          await sleep(Number.isFinite(ra) && ra > 0 ? ra * 1000 : Math.min(3000, 400 * 2 ** attempt));
          continue;
        }
        if (!res.ok) return null;
        return await res.json();
      }
    } catch (_) {
      return null;
    } finally {
      W.__EPASS_INTERNAL_CART_MUTATION__ = false;
    }
  }

  async function runPipeline() {
    if (pipelineRunning) {
      schedulePipeline(pendingBase);
      return;
    }
    pipelineRunning = true;

    const base = pendingBase || {};
    pendingBase = null;

    try {
      emit("epass:add_to_cart:done", base);

      let reconciled = null;
      if (W.EPASS_RECONCILE && typeof W.EPASS_RECONCILE.run === "function") {
        try {
          // EPASS_RECONCILE.run() émet déjà epass:cart:reconciled + refresh thème
          reconciled = await W.EPASS_RECONCILE.run();
        } catch (e) {
          warn("reconcile error", e);
        }
      } else {
        log("EPASS_RECONCILE indisponible (pas encore chargé ?)");
      }

      const cart =
        reconciled && reconciled.payload ? reconciled.payload : await safeGetCart();

      emit("epass:add_to_cart:finished", {
        ...base,
        cart,
        reconciled: !!(reconciled && reconciled.payload),
      });

      log("pipeline done", { base, changed: !!(reconciled && reconciled.changed) });
    } catch (e) {
      warn("pipeline error", e);
    } finally {
      pipelineRunning = false;
      lastPipelineAt = Date.now();
    }
  }

  // =========================================================
  // Détection "panier non réconcilié" (pour les filets de sécurité)
  // = une ligne Kaching (__kaching_bundles) qui porte encore un payload brut.
  // =========================================================
  function cartNeedsReconcile(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    for (const item of cart.items) {
      const props = item.properties || {};
      if (typeof props.__kaching_bundles !== "string") continue;
      for (const key of Object.keys(props)) {
        if (!/^_epass_person/i.test(key)) continue;
        const v = props[key];
        if (typeof v === "string" && v.trim()) return true;
      }
    }
    return false;
  }

  async function safetyReconcile(reason) {
    if (isInternal() || pipelineRunning) return;
    // Si un pipeline vient de tourner, on ne re-sonde pas le panier (anti-429).
    if (Date.now() - lastPipelineAt < SAFETY_COOLDOWN_MS) return;
    const cart = await safeGetCart();
    if (cart && cartNeedsReconcile(cart)) {
      log("safety reconcile ->", reason);
      schedulePipeline({ transport: reason || "safety" });
    }
  }

  // =========================================================
  // 1) WRAPPER FETCH (un seul, capture le fetch natif une fois)
  // =========================================================
  const nativeFetch =
    typeof W.fetch === "function" ? W.fetch.bind(W) : null;

  // innerFetch est figé à l'installation : si un tiers (Kaching) réassigne
  // window.fetch APRÈS nous, son wrapper englobera coreFetch (ordre correct,
  // pas de récursion). Si Kaching patche AVANT nous, on englobe le sien.
  let innerFetch = nativeFetch;

  async function coreFetch(input, init) {
    if (!innerFetch) throw new Error("[EPASS-CORE] no native fetch");

    const url = urlOf(input);
    const method = methodOf(input, init);
    const internal = isInternal();

    let useInit = init;
    let bundleInjected = false;
    if (!internal && method === "POST" && CART_ADD_RE.test(url)) {
      stashPendingPayload();
      // 1) priorité : injection à la source sur le bundle Kaching (JSON items[])
      const bundle = maybeInjectBundle(init);
      if (bundle) {
        useInit = bundle.init;
        bundleInjected = true;
      } else {
        // 2) sinon : injection simple (perso/gbp) sur un ajout mono-produit
        useInit = maybeInjectIntoInit(input, init);
      }
    }

    const res = await innerFetch(input, useInit);

    // On n'agit QUE sur /cart/add (jamais change/update : retraits, quantités,
    // vidage…) pour ne créer aucune requête superflue (anti-429 Cloudflare).
    if (!internal && method === "POST" && CART_ADD_RE.test(url)) {
      const ok = !!(res && res.ok);
      if (ok) {
        if (bundleInjected) {
          // Lignes déjà correctes : ZÉRO requête en plus. On marque le cooldown
          // (neutralise tout filet) et on émet simplement les events.
          lastPipelineAt = Date.now();
          emit("epass:add_to_cart:done", { transport: "fetch", url, injected: true });
          emit("epass:add_to_cart:finished", { transport: "fetch", url, injected: true });
        } else {
          // Filet : si l'injection n'a pas pu se faire, on réconcilie a posteriori.
          schedulePipeline({ transport: "fetch", url, status: res.status, ok });
        }
      }
    }

    return res;
  }

  if (nativeFetch) {
    coreFetch.__epassCore = true;
    W.fetch = coreFetch;
  }

  // =========================================================
  // 2) WRAPPER XHR (prototype = indépendant de l'ordre de chargement)
  // =========================================================
  if (!W.__EPASS_CORE_XHR_PATCHED__) {
    W.__EPASS_CORE_XHR_PATCHED__ = true;

    const _open = XMLHttpRequest.prototype.open;
    const _send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__epassMethod = String(method || "GET").toUpperCase();
      this.__epassUrl = String(url || "");
      return _open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      try {
        const url = this.__epassUrl || "";
        const method = this.__epassMethod || "GET";

        // Injection sur /cart/add (FormData / string), hors interne & hors items[]
        if (!isInternal() && method === "POST" && CART_ADD_RE.test(url) && body) {
          stashPendingPayload();
          const props = collectInjectableProps();
          if (Object.keys(props).length) {
            if (body instanceof FormData) {
              let isItems = false;
              for (const k of body.keys()) {
                if (/^items\[/.test(k)) { isItems = true; break; }
              }
              if (!isItems) {
                for (const [name, val] of Object.entries(props)) {
                  body.set(`properties[${name}]`, val);
                }
              }
            } else if (typeof body === "string") {
              const usp = new URLSearchParams(body);
              if (!usp.has("items[0][id]")) {
                for (const [name, val] of Object.entries(props)) {
                  usp.set(`properties[${name}]`, val);
                }
                arguments[0] = usp.toString();
              }
            }
          }
        }

        if (!isInternal() && method === "POST" && CART_ADD_RE.test(url)) {
          this.addEventListener("loadend", () => {
            if (isInternal()) return;
            const ok = this.status >= 200 && this.status < 300;
            if (ok) schedulePipeline({ transport: "xhr", url, status: this.status, ok });
          });
        }
      } catch (e) {
        warn("xhr send error (ignored)", e);
      }
      return _send.apply(this, arguments);
    };
  }

  // =========================================================
  // 3) FILETS DE SÉCURITÉ (couvrent les ajouts Kaching qui
  //    contourneraient fetch/XHR en utilisant un fetch capturé tôt)
  // =========================================================
  function wirePubSub() {
    try {
      if (W.PUB_SUB_EVENTS && W.PUB_SUB_EVENTS.cartUpdate && typeof W.subscribe === "function") {
        W.subscribe(W.PUB_SUB_EVENTS.cartUpdate, (data) => {
          const src = data && data.source ? String(data.source) : "";
          if (src.startsWith("epass")) return; // évite la boucle
          if (isInternal()) return;
          safetyReconcile("pubsub");
        });
        log("PUB_SUB cartUpdate branché");
        return true;
      }
    } catch (_) {}
    return false;
  }

  function wirePubSubWhenReady() {
    if (wirePubSub()) return;
    let tries = 0;
    const t = setInterval(() => {
      if (wirePubSub() || ++tries >= 20) clearInterval(t);
    }, 250);
  }

  const ADD_BTN_SELECTOR = [
    '[name="add"]',
    'product-form [type="submit"]',
    '.product-form__submit',
    '[data-add-to-cart]',
    '.add-to-cart',
    '.kaching-bundles',
    '.kaching-bundles__cta',
    '.kaching-bundles__footer',
    'kaching-bundles-block',
  ].join(",");

  function wireClickSafetyNet() {
    document.addEventListener(
      "click",
      (e) => {
        const t = e.target;
        if (!t || !t.closest) return;
        if (!t.closest(ADD_BTN_SELECTOR)) return;
        stashPendingPayload(); // capture le payload au plus tôt (avant tout re-render)
        // Filet anti-contournement (rare, car le wrapper attrape déjà l'ajout).
        // Gardé léger et espacé ; le cooldown évite tout doublon de requêtes.
        [1500, 3500].forEach((d) => setTimeout(() => safetyReconcile("click"), d));
      },
      true
    );
  }

  function boot() {
    // Avec l'injection à la source, les filets de sécurité (sondages /cart.js
    // au clic / sur cartUpdate) ne servent plus et généraient des requêtes
    // panier EN PARALLÈLE -> 429 (Cloudflare/Shopify). On ne les branche plus.
    log("EPASS-CORE prêt");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  // API publique (debug / déclenchement manuel)
  W.EPASS_CORE = {
    reconcileNow: () => schedulePipeline({ transport: "manual" }),
    safetyReconcile: () => safetyReconcile("manual"),
    cartNeedsReconcile,
    version: 1,
  };
})();
